import dayjs, { OpUnitType } from "dayjs";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(duration);
dayjs.extend(isBetween);

export const dateLocale = ({
  date,
  locale = "id-ID",
  timeZone = "Asia/Jakarta",
  withWeekDay,
  withTime,
  isShort,
}: {
  date?: Date | string;
  locale?: string;
  timeZone?: string;
  withWeekDay?: boolean;
  withTime?: boolean;
  isShort?: boolean;
}) => {
  const d = date
    ? typeof date == "string"
      ? new Date(date)
      : date
    : new Date();
  let opts: Intl.DateTimeFormatOptions = {
    timeZone,
    year: "numeric",
    month: isShort ? "short" : "long",
    day: "numeric",
    hour12: false,
  };

  if (withWeekDay) opts = { ...opts, weekday: isShort ? "short" : "long" };

  if (withTime) opts = { ...opts, hour: "numeric", minute: "numeric" };

  return new Intl.DateTimeFormat(locale, opts).format(d);
};

/**
 * Returns the number of days in a given month and year.
 * @param month - The month as an integer (1 for January, 2 for February, etc.).
 * @param year - The year as an integer.
 * @returns The number of days in the month.
 */
export function getDaysInMonth(month: number, year: number): number {
  if (month < 1 || month > 12) {
    throw new Error("Invalid month");
  }

  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Check for leap year
  if (
    month === 2 &&
    ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)
  ) {
    return 29;
  }

  return daysInMonth[month - 1];
}

/**
 * Get the start and end dates of a given month and year.
 *
 * @param {number} year - The year for which to get the start and end dates.
 * @param {number} month - The month (1-12) for which to get the start and end dates.
 * @returns {{ startDate: Date, endDate: Date }} An object containing the start and end dates of the month.
 */
export function getStartAndEndDateOfMonth(date: Date | string): {
  startDate: Date;
  endDate: Date;
} {
  if (!date) {
    return { startDate: new Date(), endDate: new Date() };
  }
  const day = new Date(date);

  const startDate = new Date(day.getFullYear(), day.getMonth(), 1);
  const endDate = new Date(day.getFullYear(), day.getMonth() + 1, 0);

  return { startDate, endDate };
}

export function isWeekend(date: Date | string) {
  const day = new Date(date).getDay();

  return [0, 6].includes(day);
}

export function getStartAndEndOfDay(date: Date | string): {
  startOfDay: string;
  endOfDay: string;
} {
  const day = dayjs(date);

  if (!day.isValid()) {
    return { startOfDay: "", endOfDay: "" };
  }

  const startOfDay = day.startOf("day").format("YYYY-MM-DD HH:mm:ss");

  const endOfDay = day.endOf("day").format("YYYY-MM-DD HH:mm:ss");

  return {
    startOfDay,
    endOfDay,
  };
}

/**
 * Formats a given date or the current date into a string with the format "DD/MM/YYYY".
 *
 * @param {string | Date} [date] - The date to format. If not provided, the current date will be used.
 * @returns {string} - The formatted date string in the format "DD/MM/YYYY".
 */
export const getDateSlash = (date?: string | Date): string => {
  // Create a Date object from the provided date or use the current date if no date is provided
  let getDate = new Date();

  // Check if the date parameter is provided and is truthy
  if (date) {
    try {
      // Attempt to create a Date object from the provided date
      getDate = new Date(date);

      // Check if the created date is valid
      if (isNaN(getDate.getTime())) {
        // If the date is invalid, use the current date
        getDate = new Date();
      }
    } catch (e) {
      // Log an error message if an exception occurs
      console.error("set date slash error:", e);
    }
  }

  // Get the day of the month and pad it with a leading zero if necessary
  const day = getDate.getDate().toString().padStart(2, "0");

  // Get the month (0-based index) and pad it with a leading zero if necessary
  const month = (getDate.getMonth() + 1).toString().padStart(2, "0");

  // Get the full year
  const year = getDate.getFullYear().toString();

  // Return the formatted date string in the format "DD/MM/YYYY"
  return `${day}/${month}/${year}`;
};

/**
 * Calculates the difference between two times and returns the result in the format `${hours} Jam ${minutes} menit`.
 *
 * @param {string} start - The start time in the format `HH:mm`.
 * @param {string} end - The end time in the format `HH:mm`.
 * @returns {string} - The formatted time difference in the format `${hours} Jam ${minutes} menit`.
 */
export const calculateTimeDifference = (start: string, end: string): string => {
  // Get the current date in the format 'YYYY-MM-DD'
  const currentDate = dayjs().format("YYYY-MM-DD");

  // Parse the start and end times with the current date
  const startTime = dayjs(
    `${currentDate} ${start.slice(0, 5)}`,
    "YYYY-MM-DD HH:mm",
  );
  let endTime = dayjs(`${currentDate} ${end.slice(0, 5)}`, "YYYY-MM-DD HH:mm");

  // Check if the parsed times are valid
  if (!startTime.isValid() || !endTime.isValid()) {
    return "0 Jam 0 Menit";
  }

  // If end time is before start time, add one day to end time
  if (endTime.isBefore(startTime)) {
    endTime = endTime.add(1, "day");
  }

  // Calculate the difference in milliseconds
  const diff = endTime.diff(startTime);

  // Convert the difference to a duration object
  const durationObj = dayjs.duration(diff);

  // Extract hours and minutes from the duration object
  const hours = Math.floor(durationObj.asHours());
  const minutes = durationObj.minutes();

  // Return the formatted time difference
  return `${hours} Jam ${minutes} Menit`;
};

/**
 * Compares two times and returns true if the start time is greater than the end time.
 *
 * @param {string} start - The start time in the format `HH:mm`.
 * @param {string} end - The end time in the format `HH:mm`.
 * @returns {boolean} - Returns true if the start time is greater than the end time, otherwise false.
 */
export const isStartTimeGreaterThanEndTime = (
  start?: string,
  end?: string,
): boolean => {
  // Get the current date in the format 'YYYY-MM-DD'
  const currentDate = dayjs().format("YYYY-MM-DD");

  // Parse the start and end times with the current date
  const startTime = dayjs(
    `${currentDate} ${(start ?? "").slice(0, 5)}`,
    "YYYY-MM-DD HH:mm",
  );
  const endTime = dayjs(
    `${currentDate} ${(end ?? "").slice(0, 5)}`,
    "YYYY-MM-DD HH:mm",
  );

  // Check if the parsed times are valid
  if (!startTime.isValid() || !endTime.isValid()) {
    return false;
  }

  // Compare the start and end times
  return startTime.isAfter(endTime);
};

const dateTimeFormat = "YYYY-MM-DD HH:mm:ss";
export const isDateTimeBetween = ({
  target,
  prev,
  next,
  precision = "second",
}: {
  target?: string;
  prev: string;
  next: string;
  precision?: OpUnitType;
}): boolean => {
  let targetDate: string;
  if (!target) {
    targetDate = dayjs().format(dateTimeFormat);
  } else {
    targetDate = dayjs(target).format(dateTimeFormat);
  }

  const prevLimit = dayjs(prev).format(dateTimeFormat);
  const nextLimit = dayjs(next).format(dateTimeFormat);

  return dayjs(targetDate).isBetween(prevLimit, nextLimit, precision);
};

export const getStartAndEndOfDayWithTimeZone = (
  date: Date | string,
): {
  startOfDay: string;
  endOfDay: string;
} => {
  const day = dayjs(date);

  if (!day.isValid()) {
    return { startOfDay: "", endOfDay: "" };
  }

  return {
    startOfDay: day.startOf("day").toDate().toISOString(),
    endOfDay: day.endOf("day").toDate().toISOString(),
  };
};
